import type { QueryClient } from '@tanstack/react-query'

export const PROCESSING_POLL_INTERVAL_MS = 5000
const MEDIUM_POLL_INTERVAL_MS = 10000
const MAX_POLL_INTERVAL_MS = 30000
const MEDIUM_POLL_THRESHOLD_MS = 60 * 1000 // 1 min
const SLOW_POLL_THRESHOLD_MS = 5 * 60 * 1000 // 5 min
const STUCK_JOB_CUTOFF_MS = 30 * 60 * 1000 // 30 min

function isProcessing(status?: string): boolean {
  return status === 'pending' || status === 'processing'
}

// Backoff based on how long the job has been in its current processing run,
// measured from the server's updatedAt (which resets whenever a job flips back
// to pending/processing, so regenerations of the same jobId start fast again
// and the cutoff can't accumulate across runs): 5s for the first minute, 10s
// until 5 minutes, then 30s, stopping entirely after 30 minutes so a
// permanently-stuck job stops hammering the API. Terminal statuses stop
// immediately.
export function getProcessingRefetchInterval(status?: string, statusUpdatedAt?: string): number | false {
  if (!isProcessing(status)) return false

  const updatedAtMs = statusUpdatedAt ? new Date(statusUpdatedAt).getTime() : Number.NaN
  const elapsedMs = Number.isFinite(updatedAtMs) ? Math.max(Date.now() - updatedAtMs, 0) : 0

  if (elapsedMs > STUCK_JOB_CUTOFF_MS) return false
  if (elapsedMs < MEDIUM_POLL_THRESHOLD_MS) return PROCESSING_POLL_INTERVAL_MS
  if (elapsedMs < SLOW_POLL_THRESHOLD_MS) return MEDIUM_POLL_INTERVAL_MS
  return MAX_POLL_INTERVAL_MS
}

export async function invalidateQueryKeys(
  queryClient: QueryClient,
  ...queryKeys: ReadonlyArray<readonly unknown[]>
): Promise<void> {
  await Promise.all(queryKeys.map((queryKey) => queryClient.invalidateQueries({ queryKey })))
}
